import { Link, NavLink, useNavigate } from "react-router-dom";
import SideBarLink from "./SideBarLink";
import { Dashboardsvg } from "../SvgFile/Index";
import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import Logout from "../Component/Pages/Auth/Logout";
import { useSelector } from "react-redux";
import { HasPermission } from "../helpers/common";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Collapse,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PersonIcon from "@mui/icons-material/Person";
import SettingsIcon from "@mui/icons-material/Settings";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LockIcon from "@mui/icons-material/Lock";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { styled } from "@mui/material/styles";

const drawerPaperSx = (theme) => ({
  "& .MuiDrawer-paper": {
    width: 280,
    backgroundColor: theme.palette.shell?.nav || "#16171d",
    color: theme.palette.shell?.navText || "#e8eaed",
    borderRight: "none",
    boxShadow: "none",
    [theme.breakpoints.between("sm", "md")]: {
      width: 260, // Slightly narrower on tablet
    },
    [theme.breakpoints.down("sm")]: {
      width: 280, // Full width on mobile
    },
  },
});

const StyledDrawer = styled(Drawer)(({ theme }) => drawerPaperSx(theme));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  backgroundColor: "transparent",
  color: "#fff",
  boxShadow: "none",
  border: "none",
  "&:before": {
    display: "none",
  },
  "& .MuiAccordionSummary-root": {
    backgroundColor: "transparent",
    padding: "8px 16px",
    minHeight: "auto",
    [theme.breakpoints.between("md", "lg")]: {
      padding: "10px 16px", // Slightly more padding on tablet
    },
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.05)",
    },
    "& .MuiAccordionSummary-content": {
      margin: "8px 0",
      [theme.breakpoints.between("md", "lg")]: {
        margin: "10px 0", // Better spacing on tablet
      },
    },
  },
  "& .MuiAccordionDetails-root": {
    padding: "0 0 8px 0",
    backgroundColor: "rgba(0,0,0,0.1)",
    [theme.breakpoints.between("md", "lg")]: {
      padding: "0 0 10px 0", // Slightly more padding on tablet
    },
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  padding: "16px",
  backgroundColor: "rgba(0,0,0,0.1)",
  borderBottom: "1px solid rgba(255,255,255,0.1)",
  display: "flex",
  alignItems: "center",
  gap: "12px",
  [theme.breakpoints.between("md", "lg")]: {
    padding: "18px 16px", // Slightly more padding on tablet
    gap: "14px",
  },
}));

const UserSection = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: "rgba(0,0,0,0.2)",
  borderTop: "1px solid rgba(255,255,255,0.1)",
}));

const MenuToggle = styled(IconButton)(({ theme }) => ({
  color: "#fff",
  backgroundColor: "rgba(255,255,255,0.1)",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.2)",
  },
  [theme.breakpoints.up("lg")]: {
    display: "none",
  },
  [theme.breakpoints.between("md", "lg")]: {
    display: "flex", // Show on tablet
  },
}));

const WorkspaceLabel = styled(Typography)(({ theme }) => ({
  padding: "16px 16px 8px 16px",
  fontSize: "12px",
  fontWeight: 600,
  color: "#94a3b8",
  textTransform: "uppercase",
  letterSpacing: "1px",
  [theme.breakpoints.between("md", "lg")]: {
    padding: "18px 16px 10px 16px", // Better spacing on tablet
    fontSize: "13px",
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: "12px 16px",
  fontSize: "14px",
  fontWeight: 500,
  "&:hover": {
    backgroundColor: "rgba(0,0,0,0.04)",
  },
  "& .MuiSvgIcon-root": {
    marginRight: "12px",
    fontSize: "20px",
    color: theme.palette.text.secondary,
  },
}));

const LogoutMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: "12px 16px",
  fontSize: "14px",
  fontWeight: 500,
  color: theme.palette.error.main,
  "&:hover": {
    backgroundColor: "rgba(244, 67, 54, 0.04)",
  },
  "& .MuiSvgIcon-root": {
    marginRight: "12px",
    fontSize: "20px",
    color: theme.palette.error.main,
  },
}));

export default function Sidebar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState("panel0"); // First panel expanded by default
  const navigate = useNavigate();
  const pendingHref = useRef(null);
  const overlayQuery = theme.breakpoints.down("lg").replace(/^@media\s*/i, "");

  const admin_links = JSON.parse(localStorage.getItem("userData"))?.menu;
  const users = useSelector((state) => state.UserProfile.profile);
  const user = JSON.parse(localStorage.getItem("userData"));

  const handleDrawerToggle = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setMobileOpen((open) => !open);
  };

  const handleDrawerClose = () => {
    pendingHref.current = null;
    setMobileOpen(false);
  };

  const isOverlayNav = () => window.matchMedia(overlayQuery).matches;

  // Overlay nav lives inside each route, so a menu click unmounts this drawer
  // while its backdrop is still open. Close first, then navigate.
  const beginNavigation = (href) => {
    if (!isOverlayNav()) {
      return false;
    }
    pendingHref.current = href;
    flushSync(() => {
      setMobileOpen(false);
    });
    return true;
  };

  const handleUserMenuClick = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogoutClick = () => {
    setUserMenuAnchor(null);
    setLogoutOpen(true);
  };

  const handleLogoutClose = () => {
    setLogoutOpen(false);
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  const handleMobileNavigation = (href) => beginNavigation(href);

  useEffect(() => {
    if (mobileOpen || !pendingHref.current) {
      return undefined;
    }
    const href = pendingHref.current;
    const timer = window.setTimeout(() => {
      pendingHref.current = null;
      navigate(href);
    }, 50);
    return () => window.clearTimeout(timer);
  }, [mobileOpen, navigate]);

  useEffect(
    () => () => {
      document.body.classList.remove("mobile-menu-open");
      document.body.style.removeProperty("top");
    },
    []
  );

  const parentCategories = {
    CentralizedInvoice: "Sales", 
    // Estimate: "Sales",
    EstimationBreakdown: "Sales",
    Order: "Sales",
    Job: "Sales",
     Reports: "Sales",
    Commission: "Sales",
    DealerMargin: "Sales",
    Products: "Catalog",
    Options: "Catalog",
    Component: "Catalog",
    Material: "Catalog",
    FinanceDetails: "Catalog",
    PowderCoatColor: "Catalog",
    Partners: "Partners",
    ManageProduct: "Partners",
    Users: "People",
    Vendors: "People",
    Contact: "People",
    ArchiveCustomer: "People",
    Tenant: "People",
    Setting: "Admin",
    Tax: "Admin",
    Discount: "Admin",
    ProductType: "Admin",
    JobType: "Admin",
    JobTitle: "Admin",
    Templates: "Admin",
    PlaceHolders: "Admin",
  };

  const parentCategoriesOrder = [
    "People",
    "Sales",
    "Operations",
    "Catalog",
    "Partners",
    "Admin",
    "Settings",
  ];

  const excludedRoles = [
    "Roles",
    "Address",
    "Window",
    "Invoice",
    "JobSchedule",
    "VendorOrder",
    "Stripe",
    "GHL",
    "PartnerContact",
    "EmailConfiguration",
    "ProductMarkup",
    "JobContact",
  ];

  const groupLinksByCategory = (links) => {
    const grouped = {};

    // Group links by category, excluding some roles
    links?.forEach((item) => {
      if (excludedRoles.includes(item.roleName)) return;
      const category = parentCategories[item.roleName] || "Other";

      if (category === "Other") {
      }

      if (!grouped[category]) grouped[category] = [];
      grouped[category].push(item);
    });

    if (grouped["Other"]) {
      delete grouped["Other"];
    }

    if (
      HasPermission("GHLView") ||
      HasPermission("StripeView") ||
      HasPermission("EmailConfigurationView")
    ) {
      if (!grouped["Admin"]) grouped["Admin"] = [];
      grouped["Admin"].push({
        roleDisplayName: "Configuration",
        roleName: "Configuration", // Add this field
        url: "/configuration",
        icon: `<svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.445 1.78772C4.30061 1.02129 5.30557 0.440215 6.39667 0.0810547C6.70857 0.47206 7.1046 0.787723 7.55532 1.00457C8.00603 1.22141 8.49983 1.33386 9 1.33355C9.5001 1.33396 9.99386 1.22164 10.4446 1.00494C10.8953 0.788245 11.2914 0.472741 11.6033 0.081888C12.6944 0.441048 13.6994 1.02212 14.555 1.78855C14.3729 2.25393 14.298 2.75434 14.3358 3.25263C14.3736 3.75092 14.5231 4.2343 14.7733 4.66689C15.023 5.1003 15.3672 5.47185 15.7802 5.75391C16.1933 6.03596 16.6646 6.22127 17.1592 6.29606C17.3931 7.42013 17.3931 8.58032 17.1592 9.70439C16.1925 9.85272 15.3008 10.4211 14.7733 11.3336C14.523 11.7662 14.3734 12.2498 14.3356 12.7482C14.2978 13.2467 14.3728 13.7472 14.555 14.2127C13.6993 14.9789 12.6944 15.5597 11.6033 15.9186C11.2914 15.5277 10.8953 15.2122 10.4446 14.9955C9.99386 14.7788 9.5001 14.6665 9 14.6669C8.49983 14.6666 8.00603 14.779 7.55532 14.9959C7.1046 15.2127 6.70857 15.5284 6.39667 15.9194C5.30555 15.5603 4.30058 14.9792 3.445 14.2127C3.62727 13.7472 3.70229 13.2467 3.66449 12.7482C3.62669 12.2498 3.47704 11.7662 3.22667 11.3336C2.97691 10.9003 2.63271 10.5289 2.21965 10.247C1.8066 9.96509 1.33531 9.77991 0.840834 9.70522C0.606929 8.58087 0.606929 7.42041 0.840834 6.29606C1.33539 6.22127 1.80672 6.03596 2.21978 5.75391C2.63284 5.47185 2.977 5.1003 3.22667 4.66689C3.47686 4.2343 3.6264 3.75092 3.6642 3.25263C3.702 2.75434 3.62708 2.2531 3.445 1.78772ZM10.25 10.1644C10.5366 10.0014 10.7882 9.78339 10.9902 9.52282C11.1922 9.26225 11.3406 8.9643 11.427 8.64612C11.5134 8.32794 11.536 7.99581 11.4935 7.66886C11.451 7.34191 11.3443 7.0266 11.1794 6.74107C11.0146 6.45554 10.7949 6.20544 10.5329 6.00517C10.271 5.8049 9.97211 5.65841 9.65337 5.57414C9.33462 5.48987 9.00235 5.46948 8.67569 5.51414C8.34903 5.55881 8.03443 5.66764 7.75 5.83439C7.18048 6.16827 6.76615 6.71385 6.59741 7.35209C6.42867 7.99033 6.51922 8.66941 6.84931 9.24113C7.17941 9.81284 7.72223 10.2308 8.35934 10.4038C8.99645 10.5767 9.67611 10.4907 10.25 10.1644Z" fill="white"></path></svg>`,
        isVirtual: true,
      });
    }
    if (
      HasPermission("OptionExpressionView") ||
      HasPermission("ExpressionView")
    ) {
      if (!grouped["Catalog"]) grouped["Catalog"] = [];
      grouped["Catalog"].push({
        roleDisplayName: "Pricing Rules",
        roleName: "Pricing Rules", // Add this field
        url: "/expression",
        icon: `<svg
  width={24}
  height={24}
  viewBox="0 0 24 24"
  fill="none"
  xmlns="http://www.w3.org/2000/svg"
>
  <path
    d="M12.71 17.29C12.6608 17.2448 12.6072 17.2046 12.55 17.17C12.4989 17.1304 12.4414 17.1 12.38 17.08C12.3205 17.0496 12.2562 17.0293 12.19 17.02C11.9984 16.9795 11.7989 17.0005 11.62 17.08C11.4959 17.1249 11.3832 17.1966 11.2899 17.2899C11.1966 17.3832 11.1249 17.4959 11.08 17.62C11.024 17.7387 10.9966 17.8688 11 18C10.9984 18.13 11.0222 18.2591 11.07 18.38C11.128 18.4995 11.202 18.6105 11.29 18.71C11.3834 18.8027 11.4943 18.876 11.6161 18.9258C11.7379 18.9755 11.8684 19.0008 12 19C12.1312 19.0034 12.2613 18.976 12.38 18.92C12.5012 18.8694 12.6128 18.7983 12.71 18.71C12.8027 18.6166 12.876 18.5057 12.9258 18.3839C12.9755 18.2621 13.0008 18.1316 13 18C12.9984 17.8693 12.9712 17.7402 12.92 17.62C12.8724 17.4972 12.801 17.3851 12.71 17.29ZM8.55 13.17C8.49895 13.1304 8.44143 13.1 8.38 13.08C8.32176 13.0426 8.25747 13.0155 8.19 13C8.06172 12.9701 7.92828 12.9701 7.8 13L7.62 13.06L7.44 13.15L7.29 13.27C7.10253 13.4667 6.99858 13.7283 7 14C6.99924 14.1316 7.02446 14.2621 7.07423 14.3839C7.12399 14.5057 7.19732 14.6166 7.29 14.71C7.3872 14.7983 7.49882 14.8694 7.62 14.92C7.7715 14.9822 7.93597 15.0063 8.09895 14.9901C8.26193 14.9739 8.41843 14.9179 8.55471 14.8271C8.69099 14.7362 8.80287 14.6133 8.88051 14.4691C8.95815 14.3249 8.99918 14.1638 9 14C8.99632 13.7352 8.89273 13.4816 8.71 13.29L8.55 13.17ZM8.71 17.29C8.6149 17.199 8.50275 17.1276 8.38 17.08C8.19898 16.9961 7.99698 16.9682 7.8 17L7.62 17.06C7.55628 17.0819 7.49575 17.1122 7.44 17.15C7.38761 17.1869 7.33752 17.227 7.29 17.27C7.19896 17.3651 7.12759 17.4772 7.08 17.6C7.02709 17.7197 6.99977 17.8491 6.99977 17.98C6.99977 18.1109 7.02709 18.2403 7.08 18.36C7.13064 18.4812 7.20167 18.5928 7.29 18.69C7.38178 18.7863 7.49185 18.8633 7.61376 18.9165C7.73567 18.9698 7.86698 18.9981 8 19C8.13118 19.0034 8.26132 18.976 8.38 18.92C8.50118 18.8694 8.6128 18.7983 8.71 18.71C8.79833 18.6128 8.86936 18.5012 8.92 18.38C8.97291 18.2603 9.00023 18.1309 9.00023 18C9.00023 17.8691 8.97291 17.7397 8.92 17.62C8.87241 17.4972 8.80104 17.3851 8.71 17.29ZM11.62 13.08C11.4972 13.1276 11.3851 13.199 11.29 13.29C11.1073 13.4816 11.0037 13.7352 11 14C11.0008 14.1638 11.0418 14.3249 11.1195 14.4691C11.1971 14.6133 11.309 14.7362 11.4453 14.8271C11.5816 14.9179 11.7381 14.9739 11.9011 14.9901C12.064 15.0063 12.2285 14.9822 12.38 14.92C12.5012 14.8694 12.6128 14.7983 12.71 14.71C12.8027 14.6166 12.876 14.5057 12.9258 14.3839C12.9755 14.2621 13.0008 14.1316 13 14C12.9963 13.7352 12.8927 13.4816 12.71 13.29C12.5694 13.1512 12.3908 13.0572 12.1968 13.0199C12.0028 12.9825 11.8021 13.0034 11.62 13.08ZM16.71 17.29C16.6128 17.2017 16.5012 17.1306 16.38 17.08C16.1979 17.0034 15.9972 16.9825 15.8032 17.0199C15.6092 17.0572 15.4306 17.1512 15.29 17.29C15.199 17.3851 15.1276 17.4972 15.08 17.62C15.0271 17.7397 14.9998 17.8691 14.9998 18C14.9998 18.1309 15.0271 18.2603 15.08 18.38C15.1306 18.5012 15.2017 18.6128 15.29 18.71C15.3834 18.8027 15.4943 18.876 15.6161 18.9258C15.7379 18.9755 15.8684 19.0008 16 19C16.1312 19.0034 16.2613 18.976 16.38 18.92C16.5012 18.8694 16.6128 18.7983 16.71 18.71C16.8488 18.5694 16.9428 18.3908 16.9801 18.1968C17.0175 18.0028 16.9966 17.8021 16.92 17.62C16.8724 17.4972 16.801 17.3851 16.71 17.29ZM16 5H8C7.73478 5 7.48043 5.10536 7.29289 5.29289C7.10536 5.48043 7 5.73478 7 6V10C7 10.2652 7.10536 10.5196 7.29289 10.7071C7.48043 10.8946 7.73478 11 8 11H16C16.2652 11 16.5196 10.8946 16.7071 10.7071C16.8946 10.5196 17 10.2652 17 10V6C17 5.73478 16.8946 5.48043 16.7071 5.29289C16.5196 5.10536 16.2652 5 16 5ZM15 9H9V7H15V9ZM18 1H6C5.20435 1 4.44129 1.31607 3.87868 1.87868C3.31607 2.44129 3 3.20435 3 4V20C3 20.7956 3.31607 21.5587 3.87868 22.1213C4.44129 22.6839 5.20435 23 6 23H18C18.7956 23 19.5587 22.6839 20.1213 22.1213C20.6839 21.5587 21 20.7956 21 20V4C21 3.20435 20.6839 2.44129 20.1213 1.87868C19.5587 1.31607 18.7956 1 18 1ZM19 20C19 20.2652 18.8946 20.5196 18.7071 20.7071C18.5196 20.8946 18.2652 21 18 21H6C5.73478 21 5.48043 20.8946 5.29289 20.7071C5.10536 20.5196 5 20.2652 5 20V4C5 3.73478 5.10536 3.48043 5.29289 3.29289C5.48043 3.10536 5.73478 3 6 3H18C18.2652 3 18.5196 3.10536 18.7071 3.29289C18.8946 3.48043 19 3.73478 19 4V20ZM16.55 13.17C16.4989 13.1304 16.4414 13.1 16.38 13.08C16.3205 13.0496 16.2562 13.0293 16.19 13.02C16.0617 12.9901 15.9283 12.9901 15.8 13.02L15.62 13.08L15.44 13.17L15.29 13.29C15.1073 13.4816 15.0037 13.7352 15 14C15.0008 14.1638 15.0418 14.3249 15.1195 14.4691C15.1971 14.6133 15.309 14.7362 15.4453 14.8271C15.5816 14.9179 15.7381 14.9739 15.9011 14.9901C16.064 15.0063 16.2285 14.9822 16.38 14.92C16.5012 14.8694 16.6128 14.7983 16.71 14.71C16.8027 14.6166 16.876 14.5057 16.9258 14.3839C16.9755 14.2621 17.0008 14.1316 17 14C16.9963 13.7352 16.8927 13.4816 16.71 13.29L16.55 13.17Z"
    fill="white"
  />
</svg>
`,
        isVirtual: true,
      });
    }

    if (
      HasPermission("BladeCoverageView") ||
      HasPermission("BladeCoverageEdit") ||
      HasPermission("ExpressionView") ||
      HasPermission("ProductView")
    ) {
      if (!grouped["Catalog"]) grouped["Catalog"] = [];
      grouped["Catalog"].push({
        roleDisplayName: "Blade Coverage Chart",
        roleName: "Blade Coverage Chart",
        url: "/blade-coverage",
        icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M3 5h18v2H3V5zm0 6h18v2H3v-2zm0 6h12v2H3v-2z" fill="white"/>
</svg>`,
        isVirtual: true,
      });
    }

    if (!grouped["Settings"]) grouped["Settings"] = [];
    grouped["Settings"].push(
      {
        roleDisplayName: "Profile",
        roleName: "Profile", // Add this field
        url: "/update-profile",
        icon: `<svg
        width={24}
  height={24}
  className="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium css-i4bv87-MuiSvgIcon-root"
  focusable="false"
  aria-hidden="true"
  viewBox="0 0 24 24"
  data-testid="AccountCircleIcon"
>
  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m0 4c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6m0 14c-2.03 0-4.43-.82-6.14-2.88C7.55 15.8 9.68 15 12 15s4.45.8 6.14 2.12C16.43 19.18 14.03 20 12 20" fill="white"/>
</svg>

`,
        isVirtual: true,
      },
      {
        roleDisplayName: "Notifications",
        roleName: "Notifications", // Add this field
        url: "/notifications",
        icon: `<svg   width={24}
  height={24} viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
<path fill="white" fill-rule="evenodd" clip-rule="evenodd" d="M6.31317 12.463C6.20006 9.29213 8.60976 6.6252 11.701 6.5C14.7923 6.6252 17.202 9.29213 17.0889 12.463C17.0889 13.78 18.4841 15.063 18.525 16.383C18.525 16.4017 18.525 16.4203 18.525 16.439C18.5552 17.2847 17.9124 17.9959 17.0879 18.029H13.9757C13.9786 18.677 13.7404 19.3018 13.3098 19.776C12.8957 20.2372 12.3123 20.4996 11.701 20.4996C11.0897 20.4996 10.5064 20.2372 10.0923 19.776C9.66161 19.3018 9.42346 18.677 9.42635 18.029H6.31317C5.48869 17.9959 4.84583 17.2847 4.87602 16.439C4.87602 16.4203 4.87602 16.4017 4.87602 16.383C4.91795 15.067 6.31317 13.781 6.31317 12.463Z" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path fill="white" d="M9.42633 17.279C9.01212 17.279 8.67633 17.6148 8.67633 18.029C8.67633 18.4432 9.01212 18.779 9.42633 18.779V17.279ZM13.9757 18.779C14.3899 18.779 14.7257 18.4432 14.7257 18.029C14.7257 17.6148 14.3899 17.279 13.9757 17.279V18.779ZM12.676 5.25C13.0902 5.25 13.426 4.91421 13.426 4.5C13.426 4.08579 13.0902 3.75 12.676 3.75V5.25ZM10.726 3.75C10.3118 3.75 9.97601 4.08579 9.97601 4.5C9.97601 4.91421 10.3118 5.25 10.726 5.25V3.75ZM9.42633 18.779H13.9757V17.279H9.42633V18.779ZM12.676 3.75H10.726V5.25H12.676V3.75Z" fill="white"/>
</svg>
`,
        isVirtual: true,
      },
      {
        roleDisplayName: "Logout",
        roleName: "Logout", // Add this field
        url: "#",
        icon: `<svg
  className="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium css-i4bv87-MuiSvgIcon-root"
  focusable="false"
  aria-hidden="true"
  viewBox="0 0 24 24"
  data-testid="ExitToAppIcon"
>
  <path d="M10.09 15.59 11.5 17l5-5-5-5-1.41 1.41L12.67 11H3v2h9.67zM19 3H5c-1.11 0-2 .9-2 2v4h2V5h14v14H5v-4H3v4c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2" fill="white"/>
</svg>

`,
        isVirtual: true,
      }
    );

    const ordered = [];
    parentCategoriesOrder.forEach((category) => {
      if (grouped[category]) {
        ordered.push({ category, items: grouped[category] });
        delete grouped[category];
      }
    });

    // Push remaining categories in any order, but exclude empty "Other" category
    Object.entries(grouped).forEach(([category, items]) => {
      if (items.length > 0) {
        ordered.push({ category, items });
      }
    });

    return ordered;
  };

  // Usage:
  const groupedLinks = groupLinksByCategory(admin_links);

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {/* Logo Section */}
      <LogoContainer>
        <MenuToggle
          onClick={(e) => {
            e.stopPropagation();
            handleDrawerToggle(e);
          }}
          data-toggle-button="true"
          sx={{
            display: { xs: "flex", lg: "none" },
          }}
        >
          <MenuIcon />
        </MenuToggle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              borderRadius: 1,
            }}
          >
            <img
              src={users?.logo ? users?.logo : user?.logo}
              alt="Company Logo"
              loading="lazy"
              style={{ maxHeight: "32px", maxWidth: "100%" }}
            />
          </Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, fontSize: "14px", color: "#fff" }}
          >
            {users?.companyName ? users?.companyName : user?.companyName}
          </Typography>
        </Box>
      </LogoContainer>

      {/* Navigation Menu */}
      <Box sx={{ flex: 1, overflowY: "auto", pb: "80px" }}>
        {/* Dashboard */}
        <List sx={{ pt: 0 }}>
          <ListItem disablePadding>
            <ListItemButton
              component={NavLink}
              to="/dashboard"
              onClick={(event) => {
                if (beginNavigation("/dashboard")) {
                  event.preventDefault();
                }
              }}
              sx={{
                color: "#fff",
                "&.active": {
                  backgroundColor: "rgba(255,255,255,0.1)",
                  borderRight: "3px solid #1976d2",
                },
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.05)",
                },
              }}
            >
              <ListItemIcon sx={{ color: "#fff", minWidth: 40 }}>
                <DashboardIcon />
              </ListItemIcon>
              <ListItemText
                primary="Dashboard"
                primaryTypographyProps={{ fontSize: "14px", fontWeight: 500 }}
              />
            </ListItemButton>
          </ListItem>
        </List>

        {/* Workspace Label */}
        <WorkspaceLabel variant="overline">Workspace</WorkspaceLabel>

        {/* Menu Sections */}
        <Box sx={{ px: 1 }}>
          {groupedLinks.map(({ category, items }, index) => {
            const isFirst = index === 0; // check if first item

            return (
              <StyledAccordion
                key={index}
                expanded={expandedAccordion === `panel${index}`}
                onChange={handleAccordionChange(`panel${index}`)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
                >
                  <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                    {category}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List sx={{ py: 0 }}>
                    {items.map((item, i) => (
                      <SideBarLink
                        key={i}
                        href={item?.url}
                        name={item.roleDisplayName}
                        icon={item.icon}
                        handleClosemenu={handleMobileNavigation}
                        handleLogoutOpen={() => setLogoutOpen(true)}
                      />
                    ))}
                  </List>
                </AccordionDetails>
              </StyledAccordion>
            );
          })}
        </Box>
      </Box>

      {/* User Section */}
      <UserSection>
        <ListItem disablePadding>
          <ListItemButton onClick={handleUserMenuClick} sx={{ color: "#fff" }}>
            <ListItemIcon sx={{ color: "#fff", minWidth: 40 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: "#1976d2" }}>
                <PersonIcon sx={{ fontSize: 18 }} />
              </Avatar>
            </ListItemIcon>
            <ListItemText
              primary={`${users?.firstName || user?.firstName} ${
                users?.lastName || user?.lastName
              } ${users?.suffix || user?.suffix || ""}`}
              secondary={users?.email || user?.email}
              primaryTypographyProps={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#fff",
                noWrap: true,
              }}
              secondaryTypographyProps={{
                fontSize: "12px",
                color: "rgba(255,255,255,0.7)",
                noWrap: true,
              }}
            />
            <ExpandMoreIcon
              sx={{
                color: "#fff",
              }}
            />
          </ListItemButton>
        </ListItem>

        <Menu
          anchorEl={userMenuAnchor}
          open={Boolean(userMenuAnchor)}
          onClose={handleUserMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 220,
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              borderRadius: 2,
              zIndex: 1300,
            },
          }}
        >
          <StyledMenuItem
            component={Link}
            to="/update-profile"
            onClick={(event) => {
              handleUserMenuClose();
              if (beginNavigation("/update-profile")) {
                event.preventDefault();
              }
            }}
          >
            <AccountCircleIcon />
            Profile Info
          </StyledMenuItem>
          <StyledMenuItem
            component={Link}
            to="/chnage-password"
            onClick={(event) => {
              handleUserMenuClose();
              if (beginNavigation("/chnage-password")) {
                event.preventDefault();
              }
            }}
          >
            <LockIcon />
            Change Password
          </StyledMenuItem>
          <Divider sx={{ my: 1 }} />
          <LogoutMenuItem onClick={handleLogoutClick}>
            <ExitToAppIcon />
            Logout
          </LogoutMenuItem>
        </Menu>
      </UserSection>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      {/* Mobile/tablet overlay drawer. Exit is instant so the backdrop is gone before route change. */}
      <StyledDrawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerClose}
        transitionDuration={{ enter: 200, exit: 0 }}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block", lg: "none" },
          zIndex: (t) => t.zIndex.modal,
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
          },
        }}
      >
        <Box>{drawerContent}</Box>
      </StyledDrawer>

      {/* Desktop Drawer */}
      <StyledDrawer
        variant="permanent"
        sx={{
          display: { xs: "none", lg: "block" },
        }}
        open
      >
        {drawerContent}
      </StyledDrawer>

      {/* Mobile/Tablet Menu Button - only show when drawer is closed */}
      {(isMobile || isTablet) && !mobileOpen && (
        <IconButton
          onClick={handleDrawerToggle}
          data-toggle-button="true"
          aria-label="Open navigation menu"
          sx={{
            position: "fixed",
            top: { xs: 12, sm: 16, md: 20 },
            left: { xs: 12, sm: 16, md: 20 },
            zIndex: (t) => t.zIndex.drawer,
            bgcolor: "shell.nav",
            color: "#fff",
            width: { xs: 48, sm: 56, md: 60 },
            height: { xs: 48, sm: 56, md: 60 },
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            border: "2px solid rgba(255,255,255,0.1)",
            "&:hover": {
              bgcolor: "#0f1115",
              transform: "scale(1.05)",
              boxShadow: "0 6px 16px rgba(0,0,0,0.4)",
            },
            "&:active": {
              transform: "scale(0.95)",
            },
            transition: "all 0.2s ease-in-out",
          }}
        >
          <MenuIcon sx={{ fontSize: { xs: 20, sm: 24, md: 26 } }} />
        </IconButton>
      )}

      <Logout open={logoutOpen} handleClose={handleLogoutClose} />
    </Box>
  );
}
